import { onMount, type ComponentProps, splitProps } from "solid-js"
import { aIcons } from "./a-icons"
import { additionalIcons } from "./additional-icons"
import "./icon.css"

type IconName = keyof typeof aIcons | keyof typeof additionalIcons

export const iconNames = [...Object.keys(aIcons), ...Object.keys(additionalIcons)].sort() as IconName[]

const spriteID = "opencode-v2-icon-sprite"
const symbol = (name: IconName) => `opencode-v2-icon-${name}`
let spriteInserted = false

function getIcon(name: IconName) {
  const outline = aIcons[name as keyof typeof aIcons]
  if (outline) {
    return {
      viewBox: "0 0 24 24",
      body: outline,
      outline: true,
    }
  }
  return { body: additionalIcons[name as keyof typeof additionalIcons], viewBox: "0 0 20 20", outline: false }
}

function spriteContent() {
  return iconNames
    .map((name) => {
      const icon = getIcon(name)
      const body = icon.outline
        ? `<g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${icon.body}</g>`
        : icon.body
      return `<symbol id="${symbol(name)}" viewBox="${icon.viewBox}">${body}</symbol>`
    })
    .join("")
}

function ensureSprite() {
  if (spriteInserted) return
  if (typeof document === "undefined") return
  // Hot reload preserves the DOM sprite, but its symbols may be from an older module.
  const existing = document.getElementById(spriteID)
  if (existing) {
    existing.innerHTML = spriteContent()
    spriteInserted = true
    return
  }

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  svg.id = spriteID
  svg.setAttribute("aria-hidden", "true")
  svg.setAttribute("width", "0")
  svg.setAttribute("height", "0")
  svg.style.position = "absolute"
  svg.style.overflow = "hidden"
  svg.innerHTML = spriteContent()
  document.body.insertBefore(svg, document.body.firstChild)
  spriteInserted = true
}

export interface IconProps extends ComponentProps<"svg"> {
  name: IconName | (string & {})
  size?: "small" | "normal" | "large"
}

export function Icon(props: IconProps) {
  const [split, rest] = splitProps(props, ["name", "size"])
  const iconName = (): IconName => {
    if (aIcons[split.name as keyof typeof aIcons]) return split.name as keyof typeof aIcons
    if (additionalIcons[split.name as keyof typeof additionalIcons]) return split.name as keyof typeof additionalIcons
    return "plus"
  }
  const icon = () => getIcon(iconName())
  const pixelSize = () => (split.size === "small" ? 12 : split.size === "large" ? 20 : 16)
  onMount(ensureSprite)

  return (
    <svg
      {...rest}
      data-slot="icon-svg"
      data-directional={
        iconName() === "arrow-left" ||
        iconName() === "arrow-right" ||
        iconName() === "chevron-left" ||
        iconName() === "chevron-right"
          ? ""
          : undefined
      }
      width={rest.width ?? pixelSize()}
      height={rest.height ?? pixelSize()}
      viewBox={icon().viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={rest["aria-hidden"] ?? "true"}
    >
      <use href={`#${symbol(iconName())}`} />
    </svg>
  )
}
